import React from "react";
import {
  Document,
  Page,
  Image,
  View,
  Text,
  StyleSheet,
} from "@react-pdf/renderer";
import QRCode from "qrcode";

const styles = StyleSheet.create({
  page: {},
  view: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: 2,
  },
  qrContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "70%",
    maxWidth: "100%",
    width: "100%",
    aspectRatio: 1,
  },
  qrImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  qrCode: {
    fontSize: 5,
    fontFamily: "Courier",
    textAlign: "center",
    width: "100%",
    whiteSpace: "nowrap",
    paddingHorizontal: 2,
    marginTop: 2,
  },
  detailsContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "top",
    gap: 1,
    paddingHorizontal: 4,
  },
  qrCodeText: {
    fontSize: 4,
    fontFamily: "Courier",
    textAlign: "center",
    width: "100%",
    whiteSpace: "nowrap",
  },
  productId: {
    fontSize: 6,
    fontWeight: "bold",
  },
  productName: {
    fontSize: 8,
    fontWeight: "bold",
  },
  idAndVariantContainer: {
    display: "flex",
    flexDirection: "row",
    gap: 2,
    alignItems: "center",
  },
  productIdSmall: {
    fontSize: 6,
    fontWeight: "bold",
  },
  variantInfo: {
    fontSize: 6,
    color: "#4B5563",
  },
  price: {
    fontSize: 6,
    color: "#4B5563",
  },
});

const QRCodePage = ({ product, format = "5/2" }) => {
  const generateQRCode = async (product) => {
    try {
      let url = await QRCode.toDataURL(JSON.stringify(product?.qr_code));
      return url;
    } catch (error) {
      console.log("Error generating QRCode : ", error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const pageStyles =
    format === "3/2"
      ? {
        view: {
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: 8,
          height: "100%",
        },
        qrContainer: {
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "65%",
          aspectRatio: 1,
          width: "100%",
          maxWidth: "100%",
          alignSelf: "center",
        },
        qrImage: {
          width: "100%",
          height: "100%",
          objectFit: "contain",
        },
        qrCode: {
          fontSize: 15,
          fontFamily: "Courier",
          textAlign: "center",
          width: "100%",
          whiteSpace: "nowrap",
          paddingHorizontal: 0,
          marginTop: -30,
        },
        detailsContainer: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "top",
          gap: 10,
          paddingHorizontal: 32,
          marginTop: 8,
        },
        productId: {
          fontSize: 12,
          fontWeight: "bold",
        },
        productName: {
          fontSize: 22,
          fontWeight: "bold",
        },
        idAndVariantContainer: {
          display: "flex",
          flexDirection: "row",
          gap: 8,
          alignItems: "center",
        },
        productIdSmall: {
          fontSize: 22,
          fontWeight: "bold",
        },
        variantInfo: {
          fontSize: 22,
          color: "#4B5563",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
          width: "100%",
          lineHeight: 1.2,
        },
        price: {
          fontSize: 22,
          color: "#4B5563",
        },
      }
      : {
        view: {
          display: "flex",
          flexDirection: "row",
          gap: 2,
        },
        qrContainer: {
          width: 80,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 2,
          minHeight: 80,
        },
        qrImage: {
          width: "100%",
          height: "100%",
          objectFit: "contain",
        },
        qrCode: {
          fontSize: 5,
          fontFamily: "Courier",
          textAlign: "center",
          width: "100%",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          paddingHorizontal: 2,
        },
        detailsContainer: {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "top",
          gap: 3,
        },
        productId: {
          fontSize: 8,
          fontWeight: "bold",
          paddingTop: 8,
        },
        productName: {
          fontSize: 8,
          fontWeight: "bold",
        },
        variantInfo: {
          fontSize: 8,
          color: "#4B5563",
        },
        price: {
          fontSize: 8,
          color: "#4B5563",
          marginTop: 2,
        },
      };

  return (
    <Page size={format === "3/2" ? [400, 600] : [200, 80]} style={styles.page}>
      <View key={`view_${product?.id}`} style={pageStyles.view}>
        <View style={pageStyles.qrContainer}>
          <Image src={generateQRCode(product)} style={pageStyles.qrImage} />
          {format === "3/2" && (
            <Text style={pageStyles.qrCode}>
              {product?.qr_code.substring(0, 18)}
              {product?.qr_code.substring(18, 36)}
            </Text>
          )}
        </View>
        <View style={pageStyles.detailsContainer}>
          {format === "5/2" && (
            <Text style={pageStyles.qrCode}>
              {product?.qr_code.substring(0, 18)}
              {product?.qr_code.substring(18, 36)}
            </Text>
          )}
          <Text style={pageStyles.productName}>
            [{product?.id}] {product?.product_detail?.name}
          </Text>

          <View style={pageStyles.idAndVariantContainer}>
            {/* <Text style={pageStyles.productIdSmall}>{product?.id}</Text> */}
            <Text style={pageStyles.variantInfo}>
              {product?.color?.name} {product?.size?.name ? `- ${product?.size?.name}` : ''}
            </Text>
          </View>
          <Text style={pageStyles.price}>
            {formatPrice(product?.product_detail?.price)}
          </Text>
        </View>
      </View>
    </Page>
  );
};

const QRCodeDocument = ({ products, format = "5/2" }) => {
  console.log("Product: ", products);
  return (
    <Document>
      {Array.isArray(products) ? (
        products?.map((product) => (
          <QRCodePage product={product} format={format} />
        ))
      ) : (
        <QRCodePage product={products} format={format} />
      )}
    </Document>
  );
};

export default QRCodeDocument;
